/**
 * Was darf die angemeldete Person an einem Beitrag — für die Oberfläche.
 *
 * Die Entscheidung trifft immer der Server (content-api, mandantenbezogen).
 * Dieses Modul sorgt nur dafür, dass das Portal nichts anbietet, was der
 * Server sicher ablehnt: einem Leser keinen Edit-Modus, einem Anmerker
 * keinen Stift am Absatz.
 *
 * Stufen in content-api (2026-10-02): read < comment < write, je Mandant
 * über die Rolle im `roles`-Claim (`viewer` / `commenter` / `editor`,
 * `admin`).
 *
 * `unknown`: Die Person hat im Mandanten des Beitrags KEINE Rolle. Dann
 * kommt sie über eine Einzelfreigabe oder als Agenten-Sitzung, deren Stufe
 * das Portal nicht kennt. In dem Fall bleibt die Oberfläche wie bisher
 * offen und der Server entscheidet — so verliert niemand mit Schreib-
 * Freigabe den Edit-Knopf.
 */

import { getUser } from '../auth'

export type AccessLevel = 'write' | 'comment' | 'read' | 'unknown'

const LEGACY_TENANT_ALIASES: Record<string, string> = { '1': 'arkturian' }

function canonicalTenant(tenant: string | null | undefined): string | null {
  if (!tenant) return null
  return LEGACY_TENANT_ALIASES[tenant] ?? tenant
}

/** Stufe der angemeldeten Person im Mandanten eines Beitrags. */
export function accessLevelForTenant(tenant: string | null | undefined): AccessLevel {
  const user = getUser()
  if (!user) return 'read'
  if (user.is_superadmin) return 'write'
  const want = canonicalTenant(tenant)
  const roles = (user.roles || []).filter(
    (r) => r.sys === 'content' && canonicalTenant(r.tid) === want,
  )
  if (roles.length === 0) return 'unknown'
  if (roles.some((r) => r.role === 'editor' || r.role === 'admin')) return 'write'
  if (roles.some((r) => r.role === 'commenter')) return 'comment'
  return 'read'
}

/** Edit-Modus, Absatz-Stift, neue Sections anbieten? */
export function canEditPost(post: { partner_id?: string | null } | null | undefined): boolean {
  const level = accessLevelForTenant(post?.partner_id)
  return level === 'write' || level === 'unknown'
}

/** „Anmerken" und Antworten auf Anmerkungen anbieten? */
export function canCommentPost(post: { partner_id?: string | null } | null | undefined): boolean {
  const level = accessLevelForTenant(post?.partner_id)
  return level === 'write' || level === 'comment' || level === 'unknown'
}

/**
 * Reine Lese- oder Anmerk-Person: hat Content-Rollen, aber nirgends die
 * Schreibstufe. Für sie ist das Portal eine Dokumentensammlung, kein
 * Redaktionswerkzeug: keine Anlage, kein Löschen, eigene Startseite.
 * Ohne jede Content-Rolle (Einzelfreigabe, Agenten-Sitzung) bleibt es wie
 * bisher offen — dann entscheidet der Server.
 */
export function isReaderOnly(): boolean {
  const user = getUser()
  if (!user || user.is_superadmin) return false
  const roles = (user.roles || []).filter((r) => r.sys === 'content')
  if (roles.length === 0) return false
  return !roles.some((r) => r.role === 'editor' || r.role === 'admin')
}

/** Darf die Person irgendwo anmerken (für Hinweise auf der Startseite)? */
export function canCommentSomewhere(): boolean {
  const user = getUser()
  if (!user) return false
  if (user.is_superadmin) return true
  return (user.roles || []).some(
    (r) => r.sys === 'content' && ['commenter', 'editor', 'admin'].includes(r.role),
  )
}

/** Mandanten der Person im System content, kanonisch und ohne Dubletten. */
export function contentTenants(): string[] {
  const user = getUser()
  const out: string[] = []
  for (const r of user?.roles || []) {
    const t = r.sys === 'content' ? canonicalTenant(r.tid) : null
    if (t && !out.includes(t)) out.push(t)
  }
  return out
}
